import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as promoApi from '../../shared/services/promoCodes/promoApi';

const initialState = {
  promoCodes: [],
  selectedPromoCode: null,
  stats: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isToggling: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    totalPages: 0,
    limit: 20,
  },
};

// Async thunks
export const fetchAllPromoCodes = createAsyncThunk(
  'promoCodes/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await promoApi.getAllPromoCodes(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchPromoCodeUsageStats = createAsyncThunk(
  'promoCodes/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await promoApi.getPromoCodeUsageStats();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchPromoCodeById = createAsyncThunk(
  'promoCodes/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const data = await promoApi.getPromoCodeById(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createPromoCode = createAsyncThunk(
  'promoCodes/create',
  async (promoData, { rejectWithValue }) => {
    try {
      const data = await promoApi.createPromoCode(promoData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updatePromoCode = createAsyncThunk(
  'promoCodes/update',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const data = await promoApi.updatePromoCode(id, updates);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deletePromoCode = createAsyncThunk(
  'promoCodes/delete',
  async (id, { rejectWithValue }) => {
    try {
      await promoApi.deletePromoCode(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const togglePromoCodeStatus = createAsyncThunk(
  'promoCodes/toggleStatus',
  async (id, { rejectWithValue }) => {
    try {
      const data = await promoApi.togglePromoCodeStatus(id);
      return data.promoCode;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const resetPromoCodeUsageCount = createAsyncThunk(
  'promoCodes/resetUsage',
  async (id, { rejectWithValue }) => {
    try {
      const data = await promoApi.resetPromoCodeUsage(id);
      return data.promoCode;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const promoCodeSlice = createSlice({
  name: 'promoCodes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedPromoCode: (state) => {
      state.selectedPromoCode = null;
    },
    resetStatus: (state) => {
      state.isCreating = false;
      state.isUpdating = false;
      state.isDeleting = false;
      state.isToggling = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all promo codes
      .addCase(fetchAllPromoCodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllPromoCodes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.promoCodes = action.payload.promoCodes;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          totalPages: action.payload.totalPages,
          limit: action.payload.limit || 20,
        };
      })
      .addCase(fetchAllPromoCodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch usage stats
      .addCase(fetchPromoCodeUsageStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodeUsageStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload.stats;
      })
      .addCase(fetchPromoCodeUsageStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch promo code by ID
      .addCase(fetchPromoCodeById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodeById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPromoCode = action.payload.promoCode;
      })
      .addCase(fetchPromoCodeById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create promo code
      .addCase(createPromoCode.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createPromoCode.fulfilled, (state, action) => {
        state.isCreating = false;
        state.promoCodes.unshift(action.payload.promoCode);
        state.pagination.total += 1;
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      // Update promo code
      .addCase(updatePromoCode.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updatePromoCode.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.promoCodes.findIndex(
          (pc) => pc._id === action.payload.promoCode._id
        );
        if (index !== -1) {
          state.promoCodes[index] = action.payload.promoCode;
        }
        if (state.selectedPromoCode?._id === action.payload.promoCode._id) {
          state.selectedPromoCode = action.payload.promoCode;
        }
      })
      .addCase(updatePromoCode.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Delete promo code
      .addCase(deletePromoCode.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deletePromoCode.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.promoCodes = state.promoCodes.filter((pc) => pc._id !== action.payload);
        state.pagination.total -= 1;
      })
      .addCase(deletePromoCode.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // Toggle status
      .addCase(togglePromoCodeStatus.pending, (state) => {
        state.isToggling = true;
        state.error = null;
      })
      .addCase(togglePromoCodeStatus.fulfilled, (state, action) => {
        state.isToggling = false;
        const index = state.promoCodes.findIndex((pc) => pc._id === action.payload._id);
        if (index !== -1) {
          state.promoCodes[index] = action.payload;
        }
        if (state.selectedPromoCode?._id === action.payload._id) {
          state.selectedPromoCode = action.payload;
        }
      })
      .addCase(togglePromoCodeStatus.rejected, (state, action) => {
        state.isToggling = false;
        state.error = action.payload;
      })

      // Reset usage count
      .addCase(resetPromoCodeUsageCount.fulfilled, (state, action) => {
        const index = state.promoCodes.findIndex((pc) => pc._id === action.payload._id);
        if (index !== -1) {
          state.promoCodes[index] = action.payload;
        }
      });
  },
});

export const { clearError, clearSelectedPromoCode, resetStatus } = promoCodeSlice.actions;
export default promoCodeSlice.reducer;
